import { ErrorView } from "@/components/ui/layout/error/errorView"
import { Text } from "@/components/ui/baseUi/text"
import {
  TransactionAmountCell,
  TransactionDateCell,
  TransactionFeeCell,
  TransactionPaymentMethodCell,
  TransactionProductCell,
  TransactionStatusCell,
  TransactionUserCell,
} from "@/modules/finances/components/transactionTableComponents"
import { TransactionColumnToSort } from "@/modules/finances/utils/financeUtils"
import { cn } from "@/modules/lib/utils"
import { ColumnSortingOrder } from "@/modules/types/datatableTypes"
import { HeaderTemplate } from "@/modules/ui/components/dataTables/templates/headerTemplate"
import TransactionEmptyDark from "@images/empty/transactions_empty_dark.svg"
import { word } from "@modules/core/utils/i18n"
import { TransactionEntity } from "@modules/finances/entities/transactionEntity"
import { useTransactions } from "@modules/finances/hooks/useFinance"
import { useFinanceSidebar } from "@modules/finances/hooks/useFinanceSidebar"
import { BoxTransactionsDetailsLayout } from "@modules/finances/transactions/components/boxTransactionDetailsLayout"
import { useSidebar } from "@modules/sidebar/hooks/useSidebar"
import { HUDatatable } from "@modules/ui/components/dataTables/huDatatable"
import { LoadingSpinner } from "@modules/ui/components/loading"
import { createColumnHelper } from "@tanstack/react-table"
import { CircleQuestionMark } from "lucide-react"
import { Suspense, useState } from "react"
import { ErrorBoundary } from "react-error-boundary"

const columnHelper = createColumnHelper<TransactionEntity>()

export function BoxTransactionsTable({ className, ...props }: React.ComponentProps<"div">) {
  const { sidebarNavigateTo } = useSidebar()
  const [selectedTransaction, setSelectedTransaction] = useState<TransactionEntity>()
  const { navigateToFinanceFeesLayout } = useFinanceSidebar()

  const {
    data: transactions,
    pageSize,
    setPageSize,
    handlePreviousPage,
    handleNextPage,
    hasNextPage,
    hasPreviousPage,
    totalElements,
    setSorting,
    sortBy,
    sortOrder,
    hasFilters,
  } = useTransactions()

  const onSelectedTransaction = async (e: TransactionEntity) => {
    await sidebarNavigateTo({
      content: (
        <ErrorBoundary fallback={<ErrorView />}>
          <Suspense fallback={<LoadingSpinner />}>
            <BoxTransactionsDetailsLayout transactionId={e.transactionId} />
          </Suspense>
        </ErrorBoundary>
      ),
      onHide: () => {
        setSelectedTransaction(undefined)
        return Promise.resolve(true)
      },
      replaceAllComponents: true,
    }).then(() => {
      setSelectedTransaction(e)
    })
  }

  const columns = [
    columnHelper.accessor("user", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.name")}
          variant={
            sortBy === TransactionColumnToSort.USER
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(TransactionColumnToSort.USER, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(TransactionColumnToSort.USER, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionUserCell transaction={transaction} />,
    }),
    columnHelper.accessor("date", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.date")}
          variant={
            sortBy === TransactionColumnToSort.DATE
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(TransactionColumnToSort.DATE, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(TransactionColumnToSort.DATE, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionDateCell transaction={transaction} />,
    }),
    columnHelper.accessor("status", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.status")}
          variant={
            sortBy === TransactionColumnToSort.STATUS
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(TransactionColumnToSort.STATUS, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(TransactionColumnToSort.STATUS, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionStatusCell transaction={transaction} />,
    }),
    columnHelper.accessor("product", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.offer")}
          variant={
            sortBy === TransactionColumnToSort.PRODUCT
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(TransactionColumnToSort.PRODUCT, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(TransactionColumnToSort.PRODUCT, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionProductCell transaction={transaction} />,
    }),
    columnHelper.accessor("amount", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.price")}
          variant={
            sortBy === TransactionColumnToSort.AMOUNT
              ? sortOrder === ColumnSortingOrder.ASC
                ? "numberSortAsc"
                : "numberSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(TransactionColumnToSort.AMOUNT, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(TransactionColumnToSort.AMOUNT, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionAmountCell transaction={transaction} />,
    }),
    columnHelper.accessor("fee", {
      header: () => (
        <Text
          className="flex cursor-pointer flex-row items-center"
          fontStyle="LL"
          onClick={() => navigateToFinanceFeesLayout(false)}
        >
          {word("finances.page.table.header.fee")}
          <CircleQuestionMark className="ml-2 size-3.5 text-textDefault-caption" />
        </Text>
      ),
      cell: ({ row: { original: transaction } }) => <TransactionFeeCell transaction={transaction} />,
    }),
    columnHelper.accessor("paymentMethodType", {
      header: () => (
        <HeaderTemplate
          title={word("finances.page.table.header.paymentMethod")}
          variant={
            sortBy === TransactionColumnToSort.PAYMENT_METHOD
              ? sortOrder === ColumnSortingOrder.ASC
                ? "stringSortAsc"
                : "stringSortDesc"
              : "default"
          }
          onSortAsc={() => setSorting(TransactionColumnToSort.PAYMENT_METHOD, ColumnSortingOrder.ASC)}
          onSortDesc={() => setSorting(TransactionColumnToSort.PAYMENT_METHOD, ColumnSortingOrder.DESC)}
        />
      ),
      cell: ({ row: { original: transaction } }) => <TransactionPaymentMethodCell transaction={transaction} />,
    }),
  ]

  return (
    <div className={cn("flex grow flex-col", className)} {...props}>
      {transactions.length === 0 ? (
        <BoxTransactionsEmptyView
          title={
            hasFilters
              ? word("finances.transactions.empty.withFilters.title")
              : word("finances.transactions.empty.title")
          }
          description={word("finances.transactions.empty.message")}
        />
      ) : (
        <>
          <HUDatatable
            data={transactions}
            columns={columns}
            pageSize={pageSize}
            setPageSize={setPageSize}
            handlePreviousPage={handlePreviousPage}
            handleNextPage={handleNextPage}
            hasNextPage={hasNextPage}
            hasPreviousPage={hasPreviousPage}
            handleRowSelectionChange={(row: TransactionEntity) => void onSelectedTransaction(row)}
            selectedRowIndex={transactions.findIndex(
              (transaction) => transaction.transactionId === selectedTransaction?.transactionId,
            )}
            totalElements={totalElements}
            className="*:table-fixed"
          />
        </>
      )}
    </div>
  )
}

function BoxTransactionsEmptyView({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 rounded-4xl bg-surface-background-secondary px-4 py-6">
      <img src={TransactionEmptyDark} alt="Transaction Empty Data" className="max-w-[60%]" />
      <Text fontStyle="HS">{title}</Text>
      <Text fontStyle="BM">{description}</Text>
    </div>
  )
}
